import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { AuthContext } from '../../contexts/AuthContext';

function slugify(str=''){ 
  const m={å:'a',ä:'a',ö:'o',Å:'a',Ä:'a',Ö:'o'};
  return (str.replace(/[ÅÄÖåäö]/g,ch=>m[ch]||ch)
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .toLowerCase().replace(/[^a-z0-9]+/g,'-')
    .replace(/^-+|-+$/g,'')||'website');
}

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get('token');
  const { user, setUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [site, setSite] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setErr(t('invitations.missingToken')); setLoading(false); return; }
      try {
        const { data } = await api.get(`/invitations/preview`, { params: { token } });
        if (!alive) return;
        setInvite(data.invite);
        setSite(data.website);
      } catch (e) {
        if (!alive) return;
        setErr(t('invitations.invalidOrExpiredInvite'));
      } finally {
        alive && setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, t]);

  const [tab, setTab] = useState('login'); // 'login' | 'register'

  async function acceptNow() {
    try {
      const { data } = await api.post('/invitations/accept', { token });
      // Update auth context (websites, username)
      setUser(prev => ({
        ...prev,
        auth: true,
        name: prev?.name || data?.user?.username,
        username: data?.user?.username,
        websites: data?.user?.websites || []
      }));
      // Redirect to site
      const slug = slugify((data.website?.title) || '');
      const owner = data.user?.username || user?.username;
      if (owner && slug) navigate(`/${owner}/${slug}`, { replace: true });
      else navigate('/', { replace: true });
    } catch {
      setErr(t('invitations.failedToAcceptInvite'));
    }
  }

  if (loading) return <div className="p-6">{t('loading')}</div>;
  if (err) return <div className="p-6 text-red-600">{err}</div>;
  if (!invite || !site) return <div className="p-6">{t('invitations.inviteNotFound')}.</div>;

  const email = invite.email;

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white border rounded shadow p-6 space-y-6">
        <header>
          <h1 className="text-2xl font-semibold mb-1">{t('invitations.youAreInvited')}</h1>
          <p className="text-gray-700">
            {t('invitations.adminAccessTo', { title: site.title, name: site.name })}
          </p>
          <p className="text-gray-600 text-sm mt-1">
            {t('invitations.invitationFor')}: <span className="font-mono">{email}</span>
          </p>
        </header>

        {user?.auth ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              {t('invitations.loggedInAsNotice', { username: user?.username, email })}
            </div>
            <button onClick={acceptNow} className="px-4 py-2 bg-blue-600 text-white rounded">
              {t('invitations.acceptInvitation')}
            </button>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <button
                className={`px-3 py-1 rounded ${tab==='login'?'bg-gray-800 text-white':'bg-gray-100'}`}
                onClick={()=>setTab('login')}
              >
                {t('auth.logIn')}
              </button>
              <button
                className={`px-3 py-1 rounded ${tab==='register'?'bg-gray-800 text-white':'bg-gray-100'}`}
                onClick={()=>setTab('register')}
              >
                {t('auth.register')}
              </button>
            </div>

            {tab === 'login'
              ? <LoginForm onSuccess={acceptNow} lockEmail={email} />
              : <RegisterForm onSuccess={acceptNow} lockEmail={email} />
            }
          </>
        )}
      </div>
    </div>
  );
}

function LoginForm({ onSuccess, lockEmail }) {
  const { t } = useTranslation();
  const { setUser } = useContext(AuthContext);
  const [username, setUsername] = useState(''); // login by username
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e){
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { username, password, stayLoggedIn: true });
      if (data.code === '00' && data.user?.username) {
        setUser({ auth: true, name: data.user.username, username: data.user.username, websites: data.user.websites || [] });
        await onSuccess();
      } else setErr(data.msg || t('auth.loginFailed'));
    } catch (e) { setErr(e?.response?.data?.msg || t('auth.loginFailed')); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="block text-sm font-medium">{t('auth.username')}</label>
        <input className="w-full border rounded px-3 py-2" value={username} onChange={e=>setUsername(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium">{t('auth.password')}</label>
        <input className="w-full border rounded px-3 py-2" type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
      </div>
      {err && <div className="text-red-600 text-sm">{err}</div>}
      <button disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded">
        {loading ? t('auth.loggingIn') : t('auth.loginAndAccept')}
      </button>
      {lockEmail && (
        <p className="text-xs text-gray-600 mt-2">
          {t('invitations.noteEmailMustMatch', { email: lockEmail })}
        </p>
      )}
    </form>
  );
}

function RegisterForm({ onSuccess, lockEmail }) {
  const { t } = useTranslation();
  const { setUser } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [email] = useState(lockEmail || '');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e){
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/register', { username, email, password });
      if (data.code === '00' && data.user?.username) {
        setUser({ auth: true, name: data.user.username, username: data.user.username, websites: data.user.websites || [] });
        await onSuccess();
      } else setErr(data.msg || t('auth.registrationFailed'));
    } catch (e) { setErr(e?.response?.data?.msg || t('auth.registrationFailed')); }
    finally { setLoading(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="block text-sm font-medium">{t('auth.username')}</label>
        <input className="w-full border rounded px-3 py-2" value={username} onChange={e=>setUsername(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium">{t('auth.email')}</label>
        <input className="w-full border rounded px-3 py-2 bg-gray-100" value={email} readOnly />
      </div>
      <div>
        <label className="block text-sm font-medium">{t('auth.password')}</label>
        <input className="w-full border rounded px-3 py-2" type="password" value={password} onChange={e=>setPassword(e.target.value)} required />
      </div>
      {err && <div className="text-red-600 text-sm">{err}</div>}
      <button disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded">
        {loading ? t('invitations.creating') : t('invitations.createAccountAndAccept')}
      </button>
    </form>
  );
}
